// Customer-uploaded artwork DTOs. The portal surface only ever sees
// these — provider-specific shape (Azure SAS params, S3 keys) is
// translated at the BlobStorageProvider boundary and never leaks here.
// All valid scan results — used by Zod parsers + runtime guards at the
// register boundary.
export const ALL_BARCODE_SCANS = ['found', 'missing', 'unknown'];
// All valid roles — used by Zod parsers + runtime guards.
export const ALL_COMPONENT_ROLES = [
    'general',
    'front',
    'back',
    'cover',
    'guts',
    'dust_jacket',
    'endsheet',
    'tabs',
    'insert',
    'combined',
    'disc_art',
    'packaging',
    'component',
    'emboss',
    'foil',
    'die_cut',
    'variable_data',
    'variable_data_list',
    'assembly_step_photo',
    'assembly_kit_photo',
    'kit_art',
    'custom_packaging_art',
];
// Component roles whose printed FACE plausibly carries the kind of barcode a
// scan is about — a mailing barcode, a retail UPC, or a tracking code. These
// are the outer surfaces of a piece: a cover, a single-sheet face, the
// "everything in one file" combined upload, packaging, USB body art, printed
// custom packaging (which often carries a UPC or a shipping code). Interior
// manuscript pages ('guts'), endsheets, tabs, inserts, and the specialty/data
// files (emboss / foil / variable_data*) are NOT a barcode surface, so a
// 'missing' scan on one of them must NOT raise the advisory (an interior PDF
// having no barcode is normal, not a heads-up), and a 'found' on one says
// nothing about the piece we stock.
export const BARCODE_ADVISORY_ROLES = new Set([
    'general',
    'front',
    'back',
    'cover',
    'combined',
    'dust_jacket',
    'packaging',
    'disc_art',
    'custom_packaging_art',
]);
// Does a barcode-bearing face among these files already carry a barcode? The one
// test that auto-satisfies fulfillment's mandatory barcode question, called by
// the submit gate over the quote's Asset rows and by the SPA pre-flight over the
// same list, so the two cannot disagree about which scan counts.
export function assetsCarryBarcode(assets) {
    return assets.some((a) => a.barcode_scan === 'found' &&
        BARCODE_ADVISORY_ROLES.has(a.component_role));
}
// The two assembly reference-photo roles, named so a surface that has to branch
// on one compares against a constant rather than repeating the literal. They are
// scoped differently and that difference is load-bearing: a step photo carries an
// `assembly_step_id`, unique across every recipe on the quote, while the
// finished-kit photo is step-LESS by construction and takes its scope from the
// Asset's own `quote_line_id` instead (ADR-019).
export const ASSEMBLY_STEP_PHOTO_ROLE = 'assembly_step_photo';
export const ASSEMBLY_KIT_PHOTO_ROLE = 'assembly_kit_photo';
// Customer-facing display label per role. Surfaced in the uploader's
// per-role section header and in the asset tile caption. Internal
// teams keep using the enum value in shop talk; the customer sees
// the friendly label.
//
// "Guts" is industry-correct but uncommon to customers; we keep the
// term but pair it with help text in the UI ("the interior pages of
// your book") so first-time customers aren't confused.
export const COMPONENT_ROLE_LABEL = {
    general: 'Artwork',
    front: 'Front',
    back: 'Back',
    cover: 'Cover',
    guts: 'Inside pages',
    dust_jacket: 'Dust jacket',
    endsheet: 'Endsheets',
    tabs: 'Tabs',
    insert: 'Insert',
    combined: 'Just give us all the files and we\'ll work with you: Hands-on Prep',
    disc_art: 'Disc or drive art',
    packaging: 'Packaging',
    component: 'Component',
    emboss: 'Embossing artwork',
    foil: 'Foil artwork',
    die_cut: 'Die-cut artwork',
    variable_data: 'Personalization data',
    variable_data_list: 'Variable Data spreadsheet',
    assembly_step_photo: 'Step photo or document',
    assembly_kit_photo: 'Finished kit photo',
    kit_art: 'Kit diagram, layout, or label',
    custom_packaging_art: 'Packaging artwork',
};
// Per-role one-liner explaining what it is. Surfaced as help text
// under the role section heading. Empty string means no help needed
// (the label is self-explanatory).
export const COMPONENT_ROLE_HELP = {
    general: '',
    front: 'The front face artwork.',
    back: 'The back face artwork.',
    cover: 'The outer cover artwork: front, back, and spine in one PDF.',
    guts: 'The interior pages of your book (everything inside the cover).',
    dust_jacket: 'The removable dust jacket (hardcover wraps only).',
    endsheet: 'Endsheet artwork: the inside-front and inside-back pages bound to the cover.',
    tabs: 'Printed tab inserts.',
    insert: 'A preprinted page or card inserted into the finished piece.',
    combined: 'Have a bundle you\'d rather we just sort out, or files in a format we didn\'t list: a Word document, an InDesign package, fonts, a spreadsheet, anything? Drop it all here and upload as many files as you need. Our team sorts, combines, and converts it by hand (Word → print-ready PDF included) as a hands-on service added to your order. A person reviews every file, and we\'ll reach out directly if anything needs your attention.',
    disc_art: 'Artwork printed on the disc face, or printed or laser-etched on the USB body.',
    packaging: 'Artwork for the case or its printed wrap. A two-sided wrap goes in one file.',
    component: 'A purchased or pre-made component that ships in the kit.',
    emboss: 'Art for the area to emboss. A vector file (PDF, AI, or EPS) works best.',
    foil: 'Art for the area to foil-stamp. A vector file (PDF, AI, or EPS) works best.',
    die_cut: 'Your die line or a file marking where the cut goes. A vector file (PDF, AI, or EPS) works best.',
    variable_data: 'The file with the details that change on each piece: names, addresses, or codes. A spreadsheet works best.',
    variable_data_list: 'Upload spreadsheet for names, serial numbers, or addresses',
    assembly_step_photo: 'A photo of what this step should look like, or a document that already describes it. Straight from your phone is fine.',
    assembly_kit_photo: 'A photo of the kit, or a spreadsheet of what’s in it. Straight from your phone is fine.',
    kit_art: 'Optional. Not for your parts list or BOM: list those in the items above. Photos of your build steps go on the steps themselves.',
    custom_packaging_art: 'Your art, or your logo and where it goes. PDF works best.',
};
export const FAMILY_COMPONENT_ROLES = {
    // Single-face printing — one artwork file covers it. `combined` is the
    // optional Hands-on Prep catch-all ("just give us all the files") — where a
    // partner with only a Word doc, an InDesign package, fonts, or a format we
    // didn't list drops them and prepress preps/converts by hand. Never required.
    single_sheet: [
        { role: 'general', required: true },
        { role: 'combined', required: false },
    ],
    // Label sheets. Same optional Hands-on Prep catch-all as single_sheet.
    labels: [
        { role: 'general', required: true },
        { role: 'combined', required: false },
    ],
    // Booklets — cover + guts is the canonical pattern. The `combined`
    // slot accepts a single PDF satisfying both at once. See
    // validateRoleSet() for the either-or gate.
    saddle_stitch_book: [
        { role: 'cover', required: true },
        { role: 'guts', required: true },
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    perfect_bound_book: [
        { role: 'cover', required: true },
        { role: 'guts', required: true },
        { role: 'dust_jacket', required: false },
        { role: 'endsheet', required: false },
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    // Hard-bound (case-bound) mirrors the paperback's role set exactly — cover + guts
    // required, with the optional dust-jacket + endsheet slots the hard-bound
    // cover sub-flow reveals (endsheet artwork), plus insert / combined.
    hard_bound_book: [
        { role: 'cover', required: true },
        { role: 'guts', required: true },
        { role: 'dust_jacket', required: false },
        { role: 'endsheet', required: false },
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    coil_or_wire_o_book: [
        { role: 'cover', required: true },
        { role: 'guts', required: true },
        // `tabs` is intentionally NOT a standing slot here. The Tabs finishing
        // workflow surfaces tab artwork as a CONDITIONAL dedicated slot
        // (tabs_file_reference → role 'tabs' in SchemaForm.FILE_REFERENCE_ROLE),
        // revealed only when tabs are turned on — the same pattern emboss / foil /
        // variable_data use. Listing it here would make ArtworkUploader's de-dup drop
        // the conditional slot in favor of an always-on one, breaking the
        // "upload tied to the tab feature" requirement. The 'tabs' ComponentRole and
        // its label/help stay defined (above) so historical tabs files still resolve.
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    // Loose-leaf binder content has NO printed cover — the 3-ring binder IS the
    // cover — so the cover workflow is skipped entirely: no `cover` role, straight
    // to the inside pages (`guts`). (Matches the configurator, where every
    // cover-group field is gated to coverFamilies, which excludes loose_leaf.)
    // The `cover` ComponentRole stays defined in ALL_COMPONENT_ROLES so any
    // historical loose-leaf cover file still resolves — it surfaces in the
    // ArtworkUploader "_other" bucket (disuse, not deletion).
    loose_leaf: [
        { role: 'guts', required: true },
        // `tabs` intentionally omitted — surfaced as a conditional dedicated slot,
        // see the coil_or_wire_o_book note above.
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    // Media replication — disc/drive art + packaging is the canonical pair, but
    // neither is required by the FAMILY: a pure data run (copy files to the
    // disc or drive, no carrier, no printed art) has no images to supply. Art is
    // only in play when the customer opts into printed art / custom
    // packaging, so both stay optional and the customer uploads only what
    // applies. The one exception is config-driven, not a spec: a printed-wrap
    // packaging pick requires `packaging` (validateRoleSet's `packaging_art`).
    // `insert` (booklet inside the case) is common but optional.
    // `combined` is the Hands-on Prep slot — drop everything and our
    // prepress team parses and preps it by hand. The `disc_art` role key
    // is retained as a stable identifier for historical data — its
    // customer label is "Disc or drive art", covering both the optical
    // formats and USB, exactly as media_type offers them.
    media_replication: [
        { role: 'disc_art', required: false },
        { role: 'packaging', required: false },
        { role: 'insert', required: false },
        { role: 'combined', required: false },
    ],
    // Custom packaging: no file unless it prints. A stock carton and unprinted
    // custom packaging have nothing to supply, so the family requires nothing and
    // printed packaging requires `custom_packaging_art` through validateRoleSet's
    // `custom_packaging_art` flag, the same config-driven shape as a media wrap. No
    // `combined` catch-all: the art is one file, and stock packaging would
    // otherwise carry an upload block for nothing.
    custom_packaging: [{ role: 'custom_packaging_art', required: false }],
    // An empty role set is load-bearing for the two entries below, not an
    // omission: validateRoleSet falls back to a REQUIRED `general` upload for any
    // product type it does not find in this map, which would demand artwork
    // before an assembly or a "something else" line could be priced. PRESENT with
    // nothing required is a different answer from ABSENT, and it is the answer
    // both need.
    //
    // An assembly item DOES offer artwork as of ADR-019's D7 decision — the
    // optional `kit_art` slot — and that is exactly why this entry stays empty:
    // the slot is reached through a seeded `file_reference` field, so it can be
    // offered without putting a role-set check in front of the price gate for the
    // family. Its reference PHOTOS remain separate, attached to build steps
    // (assembly_step_id) or to the line (assembly_kit_photo). `something_else`
    // captures nothing at all.
    assembly: [],
    something_else: [],
    // fulfillment is a quote-level order service rather than a product type —
    // captured as intent in the order-services section, with no per-line artwork
    // upload, so it carries no role set here.
};
// Decide whether a set of uploaded roles satisfies a product-type's
// component requirements. Special-cases the booklet product types: a
// single 'combined' file counts as both cover AND guts (the customer
// chose the "one PDF with everything" upload path).
//
// `present_roles` is the set of roles for which ≥1 non-deleted Asset
// row exists on the quote.
export const BOOKLET_PRODUCT_TYPES = new Set([
    'saddle_stitch_book',
    'perfect_bound_book',
    'hard_bound_book',
    'coil_or_wire_o_book',
    'loose_leaf',
]);
export function validateRoleSet(product_type, present_roles, opts) {
    // `Object.hasOwn` before the read: `product_type` is deliberately a loose
    // string here (a stored line's config is raw JSON), and FAMILY_COMPONENT_ROLES
    // is a plain object literal — so 'constructor' resolved to `Object` itself,
    // which is not nullish, so the `??` fallback never fired and the `for…of`
    // below threw "specs is not iterable". That surfaced as a 500 on
    // GET /api/storage/role-status, which the SPA polls after every upload.
    const specs = Object.hasOwn(FAMILY_COMPONENT_ROLES, product_type)
        ? FAMILY_COMPONENT_ROLES[product_type]
        : [{ role: 'general', required: true }];
    // Booklet escape hatch: a 'combined' file satisfies both 'cover'
    // and 'guts' at once.
    const accepted_via_combined = BOOKLET_PRODUCT_TYPES.has(product_type) && present_roles.has('combined');
    // Self-cover booklet: the cover is part of the guts, no separate cover file.
    const self_cover_satisfied = opts?.self_cover === true && BOOKLET_PRODUCT_TYPES.has(product_type);
    // The customer said the pair is one file, so cover + guts collapse to a
    // single required 'combined' upload (see the opt's comment above).
    const combined_artwork_chosen = opts?.combined_artwork === true && BOOKLET_PRODUCT_TYPES.has(product_type);
    const missing = [];
    for (const spec of specs) {
        if (!spec.required)
            continue;
        if (present_roles.has(spec.role))
            continue;
        if (accepted_via_combined && (spec.role === 'cover' || spec.role === 'guts')) {
            continue;
        }
        if (combined_artwork_chosen && (spec.role === 'cover' || spec.role === 'guts')) {
            continue;
        }
        if (self_cover_satisfied && spec.role === 'cover') {
            continue;
        }
        missing.push(spec.role);
    }
    // The collapsed pair's ONE file, standing in for the two slots the choice
    // hid. Components already uploaded before the customer ticked the box count
    // too: the requirement is one file for the pair, so a line that already holds
    // both is satisfied — we never ask for a further file on this path.
    if (combined_artwork_chosen &&
        !present_roles.has('combined') &&
        !(present_roles.has('cover') && present_roles.has('guts'))) {
        missing.push('combined');
    }
    if (opts?.packaging_art === true &&
        product_type === 'media_replication' &&
        !present_roles.has('packaging')) {
        missing.push('packaging');
    }
    if (opts?.custom_packaging_art === true && product_type === 'custom_packaging' && !present_roles.has('custom_packaging_art')) {
        missing.push('custom_packaging_art');
    }
    return {
        complete: missing.length === 0,
        missing,
        accepted_via_combined,
    };
}
// The roles this line actually DEMANDS a file for, given the same escape
// hatches validateRoleSet applies (a self-cover booklet needs no cover; a
// collapsed cover+inside pair needs one 'combined' file and neither component).
// Derived by asking validateRoleSet what is missing when nothing is uploaded,
// so there is ONE requirement rule and no parallel list to drift from it
// (R12/R13). The portal renders the asterisk + "needed before you can place the
// order" badge off this: a `file_reference` field's own `required` flag is
// always false (uploads live in Asset rows, not config), so the schema cannot
// answer the question.
export function familyRequiredRoles(product_type, opts) {
    return new Set(validateRoleSet(product_type, new Set(), opts).missing);
}
const REUSABLE_SOURCES = new Set([
    'quote_artwork',
    'company_file',
]);
// Defensive normalizer for one untrusted reusable-artwork row (Nexus → portal).
// Returns null (dropped) when there is no usable object_key or filename — a row
// the customer can neither name nor reuse is noise. Mirrors normalizeCompanyItem:
// allowlist only, so an unexpected column never reaches the SPA.
export function normalizeReusableAsset(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.object_key !== 'string' || r.object_key.length === 0)
        return null;
    const filename = typeof r.filename === 'string' && r.filename.length > 0 ? r.filename : null;
    if (!filename)
        return null;
    const role = typeof r.component_role === 'string' &&
        ALL_COMPONENT_ROLES.includes(r.component_role)
        ? r.component_role
        : null;
    return {
        object_key: r.object_key,
        filename,
        component_role: role,
        content_type: typeof r.content_type === 'string' ? r.content_type : null,
        size_bytes: typeof r.size_bytes === 'number' && Number.isFinite(r.size_bytes) && r.size_bytes > 0
            ? r.size_bytes
            : null,
        content_hash: typeof r.content_hash === 'string' ? r.content_hash : null,
        source: REUSABLE_SOURCES.has(r.source)
            ? r.source
            : 'company_file',
        source_reference: typeof r.source_reference === 'string' && r.source_reference.length > 0
            ? r.source_reference
            : null,
        created_at: typeof r.created_at === 'string' ? r.created_at : '',
        rendition: null,
    };
}
//# sourceMappingURL=asset.js.map